import { Button, Icon, Modal } from "semantic-ui-react";

import { CustomerCarAppointmentDetails, CustomerDetail, CustomerNotes } from "components/CustomerModal/components";
import "components/CustomerModal/CustomerModal.scss";
import { useCustomerServiceDetailsQuery, useUpdateCustomerMutation } from "components/CustomerModal/hooks";

import type { COMMUNICATION_CHANNELS, Customer, Dealer, DealerLocation } from "models";
import type { FormEvent } from "react";
import type { CheckboxProps } from "semantic-ui-react";

type CustomerModalProps = {
  isOpen: boolean;
  customer: Customer;
  dealer?: Dealer | null;
  location?: DealerLocation | null;
  onClose: () => void;
};

const getBasePayload = ({ id, block_communication, is_internal, preferred_communication_channel }: Customer) => ({
  id,
  block_communication,
  is_internal,
  preferred_communication_channel
});

const getCustomerName = ({ title, firstname, surname, company }: Customer) => {
  const name = [title, firstname, surname]
    .filter(Boolean)
    .map(v => v?.trim())
    .join(" ");

  return name || company;
};

export const CustomerModal = ({ isOpen, customer, dealer, location, onClose }: CustomerModalProps) => {
  const customerMutation = useUpdateCustomerMutation();
  const { data: customerWithServiceDetails } = useCustomerServiceDetailsQuery(customer.id);

  const handleChangeCheckbox = (_e: FormEvent<HTMLInputElement>, { name, checked }: CheckboxProps) => {
    if (!name || checked === undefined || !customerWithServiceDetails || !(name in customerWithServiceDetails)) return;
    customerMutation.mutate({ ...getBasePayload(customerWithServiceDetails), [name]: checked });
  };

  const handleChangePreferredCommunicationChannel = (preferred_communication_channel: COMMUNICATION_CHANNELS) => {
    if (!customerWithServiceDetails) return;
    customerMutation.mutate({ ...getBasePayload(customerWithServiceDetails), preferred_communication_channel });
  };

  if (!customerWithServiceDetails) return null;

  return (
    <Modal open={isOpen} size="large" onClose={onClose} closeOnDimmerClick={false} className="CustomerModal">
      <Modal.Header>
        <div className="CustomerModal-header">
          <span>{getCustomerName(customerWithServiceDetails)}</span>
          <Button className="-appointment-status" size="small" onClick={onClose}>
            <Icon className="xmark grey" size="big" />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Content scrolling>
        <CustomerDetail
          customer={customerWithServiceDetails}
          dealer={dealer}
          location={location}
          isUpdatingCustomer={customerMutation.isPending}
          onChangeCheckbox={handleChangeCheckbox}
          onChangePreferredCommunicationChannel={handleChangePreferredCommunicationChannel}
        />
        <CustomerNotes customer={customer} location={location} />
        <CustomerCarAppointmentDetails appointments={customerWithServiceDetails.appointments} cars={customerWithServiceDetails.cars} location={location} />
      </Modal.Content>
    </Modal>
  );
};
